import { useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';

/**
 * Global keyboard avoidance hook.
 * - On native: uses @capacitor/keyboard events
 * - On web/mobile-safari: uses visualViewport resize + focusin
 * Scrolls focused inputs into view and adds bottom padding so content
 * can scroll above the keyboard.
 */
export const useKeyboardAvoidance = () => {
  const isNative = Capacitor.isNativePlatform();
  const spacerRef = useRef(null);

  const scrollToActive = useCallback((el) => {
    if (!el) return;
    // Delay so keyboard animation finishes
    setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }, 320);
  }, []);

  useEffect(() => {
    // --- shared: scroll on focusin ---
    const handleFocusIn = (e) => {
      const t = e.target;
      if (!t) return;
      const tag = t.tagName;
      if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT' && !t.isContentEditable) return;
      // Only add keyboard-open on devices that actually have virtual keyboards
      // Desktop devices (with a mouse/pointer) don't need this behavior
      const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
      if (!hasCoarsePointer && !isNative) return;
      document.body.classList.add('keyboard-open');
      scrollToActive(t);
    };

    const handleFocusOut = (e) => {
      // Only remove class if no other input got focus
      setTimeout(() => {
        const a = document.activeElement;
        if (!a || (a.tagName !== 'INPUT' && a.tagName !== 'TEXTAREA' && a.tagName !== 'SELECT' && !a.isContentEditable)) {
          document.body.classList.remove('keyboard-open');
          removeSpacer();
        }
      }, 80);
    };

    document.addEventListener('focusin', handleFocusIn, { passive: true });
    document.addEventListener('focusout', handleFocusOut, { passive: true });

    // --- native: Capacitor Keyboard plugin ---
    let kbShowUnsub, kbHideUnsub;
    if (isNative) {
      (async () => {
        try {
          const { Keyboard } = await import('@capacitor/keyboard');
          kbShowUnsub = await Keyboard.addListener('keyboardWillShow', (info) => {
            document.body.classList.add('keyboard-open');
            document.documentElement.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
            ensureSpacer(info.keyboardHeight);
            const a = document.activeElement;
            if (a && isInputLike(a)) scrollToActive(a);
          });
          kbHideUnsub = await Keyboard.addListener('keyboardWillHide', () => {
            document.body.classList.remove('keyboard-open');
            document.documentElement.style.setProperty('--keyboard-height', '0px');
            removeSpacer();
          });
        } catch (err) {
          // Keyboard plugin not available — rely on visualViewport fallback
        }
      })();
    }

    // --- web / mobile safari: visualViewport resize ---
    const vv = window.visualViewport;
    const onVVResize = () => {
      if (!vv) return;
      const heightDiff = window.innerHeight - vv.height;
      const kbOpen = heightDiff > 100;

      if (kbOpen) {
        document.body.classList.add('keyboard-open');
        document.documentElement.style.setProperty('--keyboard-height', `${heightDiff}px`);
        ensureSpacer(heightDiff);
        const a = document.activeElement;
        if (a && isInputLike(a)) {
          setTimeout(() => a.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
        }
      } else {
        document.body.classList.remove('keyboard-open');
        document.documentElement.style.setProperty('--keyboard-height', '0px');
        removeSpacer();
      }
    };

    if (vv) {
      vv.addEventListener('resize', onVVResize);
    }

    // --- helpers ---
    function ensureSpacer(height) {
      if (!spacerRef.current) {
        spacerRef.current = document.createElement('div');
        spacerRef.current.id = 'kb-spacer';
        spacerRef.current.style.cssText = 'flex-shrink:0;pointer-events:none;width:100%;';
      }
      spacerRef.current.style.height = `${height}px`;

      // Attach spacer to the nearest scrollable parent of active element
      const active = document.activeElement;
      const parent = active ? findScrollParent(active) : null;
      const target = parent || document.getElementById('root') || document.body;
      if (spacerRef.current.parentNode !== target) {
        spacerRef.current.remove();
        target.appendChild(spacerRef.current);
      }
    }

    function removeSpacer() {
      if (spacerRef.current && spacerRef.current.parentNode) {
        spacerRef.current.remove();
      }
    }

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      if (vv) vv.removeEventListener('resize', onVVResize);
      if (kbShowUnsub) kbShowUnsub.remove();
      if (kbHideUnsub) kbHideUnsub.remove();
      removeSpacer();
      document.body.classList.remove('keyboard-open');
      document.documentElement.style.setProperty('--keyboard-height', '0px');
    };
  }, [isNative, scrollToActive]);
};

function isInputLike(el) {
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

function findScrollParent(el) {
  let node = el.parentElement;
  while (node && node !== document.body) {
    const s = window.getComputedStyle(node);
    if ((s.overflowY === 'auto' || s.overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Per-input ref hook — attach to inputs needing extra scroll-into-view on focus.
 */
export const useInputKeyboardScroll = () => {
  const inputRef = useRef(null);

  const handleFocus = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 320);
  }, []);

  useEffect(() => {
    const el = inputRef.current;
    if (el) {
      el.addEventListener('focus', handleFocus);
      return () => el.removeEventListener('focus', handleFocus);
    }
  }, [handleFocus]);

  return inputRef;
};

export default useKeyboardAvoidance;
